import React, { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation, Link } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import PreviewBanner from "@/components/PreviewBanner";
import FeedbackWidget from "@/components/FeedbackWidget";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Menu } from "lucide-react";

export default function AppLayout() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [loc.pathname]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (mobileNavOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileNavOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#7C3AED]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  if (!user.profile && loc.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;

  return (
    <div className="flex bg-white min-h-screen">
      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <main className="flex-1 min-w-0">
        {/* Mobile top bar — hidden on lg+ where the sidebar is always visible */}
        <div
          data-testid="mobile-topbar"
          className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 flex items-center justify-between px-4 h-14"
        >
          <button
            data-testid="mobile-nav-open"
            aria-label="Open menu"
            onClick={() => setMobileNavOpen(true)}
            className="p-2 -ml-2 text-gray-700 hover:text-black active:scale-95 transition-transform"
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>
          <Link to="/app" className="flex items-center gap-2" data-testid="mobile-brand">
            <span className="brand-mark"><span /><span /><span /></span>
            <span className="font-display text-[18px] tracking-[-0.02em] font-semibold leading-none">Fount</span>
          </Link>
          <div className="w-8" aria-hidden />
        </div>

        <PreviewBanner />
        <VerifyEmailBanner />
        <Outlet />
        <FeedbackWidget />
      </main>
    </div>
  );
}
